import type { RouteLocation } from 'vue-router';
import type { AgentTools } from '../runtime/types/tools';
import { setAgentToolsRegistry } from '../runtime/utils/agentTools';
import { AgentToolsRegistry } from '../runtime/utils/agentToolsRegistry';
import { beginAgentEntityNavigation } from '../runtime/utils/agentEntityLoad';

// `id` to parametr, którym `goto.entity` otwiera encję (`plugins/locator.ts`), więc tylko on wskazuje
// rekord otwarty na trasie
function entityIdOf(route: RouteLocation | undefined): string | null
{
    const id = route?.params?.id;

    return typeof id === 'string' && id.length > 0 ? id : null;
}

// Nuxt składa klucz strony ze wzorca ścieżki i wartości parametrów, a nie z napisu adresu. Ten sam
// parametr zapisany w adresie inaczej (`%3D` albo `=`) daje więc ten sam komponent strony, który nie
// montuje się od nowa i nie rejestruje drugi raz swoich wpisów
function sameParams(to: RouteLocation, from: RouteLocation | undefined): boolean
{
    return JSON.stringify(to.params ?? {}) === JSON.stringify(from?.params ?? {});
}

export default defineNuxtPlugin({
    name: 'tools',
    dependsOn: ['nuxt:router'],
    async setup()
    {
        const { $router } = useNuxtApp();
        const store = useAuthStore();
        const registry = new AgentToolsRegistry(() => ({ loaded: store.loaded, authenticated: store.authenticated }));

        // Zmiana widoku czyści poziom ulotny. Widokiem jest komponent strony, a Nuxt liczy jego klucz ze
        // ścieżki z parametrami (`<NuxtPage />` bez `page-key`), więc inny `id` pod tą samą nazwą trasy
        // to nowy komponent - montowany, zanim stary się odmontuje i zdejmie swoje wpisy. Bez czyszczenia
        // nowy widok nie mógłby się zarejestrować, a agent czytałby formularz poprzedniego rekordu. Stały
        // `key` w `definePageMeta` trzyma stronę zamontowaną przy zmianie parametrów, a samo query nie
        // wchodzi do klucza - wtedy widok zostaje ten sam, razem ze swoimi wpisami
        $router.afterEach((to: RouteLocation, from: RouteLocation, failure) =>
        {
            if (to.name != from?.name || (to.meta?.key === undefined && !sameParams(to, from)))
            {
                registry.clearEphemeral();
            }

            // Wejście na encję liczy się od zmiany `id`, nie tylko nazwy trasy: widok trzymany w pamięci
            // (`keepalive`) przechodzi z rekordu na rekord pod tą samą nazwą. Przejście odrzucone albo
            // powtórzone nie zmienia otwartej encji, więc nie zaczyna nowego wejścia
            if (!failure && (to.name != from?.name || entityIdOf(to) !== entityIdOf(from)))
            {
                beginAgentEntityNavigation(entityIdOf(to), String(to.name ?? ''));
            }
        });

        // Wylogowanie w SPA nie przeładowuje strony, a token potrafi wygasnąć sam bez wywołania akcji
        // wylogowania - sygnałem jest więc przejście stanu sesji z zalogowanej na niezalogowaną
        watch(() => store.authenticated, (value, previous) =>
        {
            if (previous === true && value === false)
            {
                registry.clearEphemeral();
            }
        });

        // Obiekt zawężony do pięciu metod kontraktu, a nie instancja rejestru - sam interfejs zawęża
        // tylko typ, więc clearEphemeral byłoby wołalne z konsoli przeglądarki. Ten obiekt idzie na
        // window, z którego korzysta wołający spoza aplikacji
        const contract: AgentTools = {
            registerTool: registry.registerTool.bind(registry),
            registerPrompt: registry.registerPrompt.bind(registry),
            registerResource: registry.registerResource.bind(registry),
            list: registry.list.bind(registry),
            call: registry.call.bind(registry)
        };

        // Poza warunkiem na window, bo kod panelu rejestruje się przez moduł i nie potrzebuje DOM -
        // pod warunkiem brak window znów po cichu gubiłby rejestracje panelu
        //
        // Moduł dostaje kontrakt poszerzony o wyrejestrowanie, a window sam kontrakt: wyrejestrowanie
        // służy kodowi panelu, który zdejmuje własną rejestrację, gdy jego element znika w obrębie
        // jednego widoku. Wołający spoza aplikacji zdejmowałby nim rejestracje widoku, którego nie
        // kontroluje - tak samo jak czyszczeniem całego poziomu ulotnego
        setAgentToolsRegistry({ ...contract, unregister: registry.unregister.bind(registry) });

        // Wołający spoza aplikacji (agent sterujący przeglądarką) potrzebuje realnego window; SPA bez
        // SSR sprawia, że w panelu warunek jest zawsze prawdziwy, ale bez niego montowanie wysypywałoby
        // się w środowisku bez DOM
        if (typeof window !== 'undefined')
        {
            window.edito = window.edito || {};
            window.edito.tools = contract;
        }
    }
});

declare global
{
    interface Window
    {
        edito?: {
            tools?: AgentTools;
        };
    }
}
